import type { AppLanguage } from "lib/language";

export type AuthPortal = "admin" | "instructor";

type PortalStrings = {
  title: string;
  subtitle: string;
  heroTitle: string;
  heroSubtitle: string;
  heroBullets: string[];
  portalLabel: string;
  otherPortalLabel: string;
  otherPortalHint: string;
  submitAdmin: string;
  submitAdminLoading: string;
  sendOtp: string;
  sendOtpLoading: string;
  verifyOtp: string;
  verifyOtpLoading: string;
  emailLabel: string;
  passwordLabel: string;
  phoneLabel: string;
  otpLabel: string;
  emailPlaceholder: string;
  passwordPlaceholder: string;
  phonePlaceholder: string;
  otpPlaceholder: string;
  stepPhone: string;
  stepVerify: string;
  resendOtp: string;
  resendIn: (seconds: number) => string;
  changePhone: string;
  otpSentTo: (phone: string) => string;
  adminTab: string;
  instructorTab: string;
};

const copy: Record<AuthPortal, Record<AppLanguage, PortalStrings>> = {
  admin: {
    ar: {
      title: "لوحة تحكم الإدارة",
      subtitle: "تسجيل دخول المشرفين ومديري النظام لإدارة المنصة التعليمية.",
      heroTitle: "إدارة منصة Easyfollow التعليمية",
      heroSubtitle: "تحكم كامل في الطلاب والمحاضرين والكورسات والإشعارات من مكان واحد.",
      heroBullets: [
        "إدارة الطلاب والمحاضرين",
        "الشؤون الأكاديمية والكيانات",
        "الأدوار والصلاحيات والإشعارات",
      ],
      portalLabel: "بوابة الإدارة",
      otherPortalLabel: "بوابة المحاضر",
      otherPortalHint: "تسجيل دخول برقم الهاتف؟",
      submitAdmin: "تسجيل الدخول",
      submitAdminLoading: "جاري تسجيل الدخول...",
      sendOtp: "",
      sendOtpLoading: "",
      verifyOtp: "",
      verifyOtpLoading: "",
      emailLabel: "البريد الإلكتروني",
      passwordLabel: "كلمة المرور",
      phoneLabel: "",
      otpLabel: "",
      emailPlaceholder: "admin@example.com",
      passwordPlaceholder: "••••••••",
      phonePlaceholder: "",
      otpPlaceholder: "",
      stepPhone: "",
      stepVerify: "",
      resendOtp: "",
      resendIn: () => "",
      changePhone: "",
      otpSentTo: () => "",
      adminTab: "الإدارة",
      instructorTab: "المحاضر",
    },
    en: {
      title: "Super Admin Dashboard",
      subtitle: "Sign in for system admins and moderators to manage the LMS platform.",
      heroTitle: "Manage your LMS at scale",
      heroSubtitle: "Full control over students, instructors, courses, and notifications.",
      heroBullets: [
        "Students & instructor management",
        "Academic affairs & entities",
        "Roles, permissions & notifications",
      ],
      portalLabel: "Admin Portal",
      otherPortalLabel: "Instructor Portal",
      otherPortalHint: "Sign in with phone OTP?",
      submitAdmin: "Sign In",
      submitAdminLoading: "Signing in...",
      sendOtp: "",
      sendOtpLoading: "",
      verifyOtp: "",
      verifyOtpLoading: "",
      emailLabel: "Email",
      passwordLabel: "Password",
      phoneLabel: "",
      otpLabel: "",
      emailPlaceholder: "admin@example.com",
      passwordPlaceholder: "••••••••",
      phonePlaceholder: "",
      otpPlaceholder: "",
      stepPhone: "",
      stepVerify: "",
      resendOtp: "",
      resendIn: () => "",
      changePhone: "",
      otpSentTo: () => "",
      adminTab: "Admin",
      instructorTab: "Instructor",
    },
  },
  instructor: {
    ar: {
      title: "بوابة المحاضر",
      subtitle: "للمحاضرين لإدارة الكورسات والطلاب والمحتوى والاختبارات والإعلانات.",
      heroTitle: "أنشئ وقدّم تجربة تعليمية متميزة",
      heroSubtitle: "أدوات عملية لإدارة الكورسات والطلاب والمحتوى والاختبارات.",
      heroBullets: [
        "إنشاء وإدارة الكورسات",
        "رفع المحتوى والفيديوهات",
        "الاختبارات والإعلانات",
      ],
      portalLabel: "بوابة المحاضر",
      otherPortalLabel: "بوابة الإدارة",
      otherPortalHint: "تسجيل دخول بالبريد وكلمة المرور؟",
      submitAdmin: "",
      submitAdminLoading: "",
      sendOtp: "إرسال رمز التحقق",
      sendOtpLoading: "جاري الإرسال...",
      verifyOtp: "تأكيد وتسجيل الدخول",
      verifyOtpLoading: "جاري التحقق...",
      emailLabel: "",
      passwordLabel: "",
      phoneLabel: "رقم الهاتف",
      otpLabel: "رمز التحقق",
      emailPlaceholder: "",
      passwordPlaceholder: "",
      phonePlaceholder: "01011112222",
      otpPlaceholder: "123456",
      stepPhone: "رقم الهاتف",
      stepVerify: "رمز التحقق",
      resendOtp: "إعادة إرسال الرمز",
      resendIn: (s) => `إعادة الإرسال خلال ${s} ث`,
      changePhone: "تغيير الرقم",
      otpSentTo: (phone) => `تم إرسال الرمز إلى ${phone}`,
      adminTab: "الإدارة",
      instructorTab: "المحاضر",
    },
    en: {
      title: "Instructor Portal",
      subtitle: "For instructors to manage courses, students, content, quizzes, and announcements.",
      heroTitle: "Teach and manage with confidence",
      heroSubtitle: "Practical tools for course authoring and student engagement.",
      heroBullets: [
        "Create and manage courses",
        "Upload content and videos",
        "Quizzes and announcements",
      ],
      portalLabel: "Instructor Portal",
      otherPortalLabel: "Admin Portal",
      otherPortalHint: "Sign in with email and password?",
      submitAdmin: "",
      submitAdminLoading: "",
      sendOtp: "Send verification code",
      sendOtpLoading: "Sending code...",
      verifyOtp: "Verify & Sign In",
      verifyOtpLoading: "Verifying...",
      emailLabel: "",
      passwordLabel: "",
      phoneLabel: "Phone number",
      otpLabel: "Verification code",
      emailPlaceholder: "",
      passwordPlaceholder: "",
      phonePlaceholder: "01011112222",
      otpPlaceholder: "123456",
      stepPhone: "Phone",
      stepVerify: "Verify",
      resendOtp: "Resend code",
      resendIn: (s) => `Resend in ${s}s`,
      changePhone: "Change number",
      otpSentTo: (phone) => `Code sent to ${phone}`,
      adminTab: "Admin",
      instructorTab: "Instructor",
    },
  },
};

export function getAuthPortalCopy(portal: AuthPortal, language: AppLanguage): PortalStrings {
  return copy[portal][language];
}
